import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, LogOut, LayoutDashboard, Settings, ChevronDown, Sun, Moon, Monitor, Globe, Check } from 'lucide-react';
import { NavItem } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from './LanguageSwitcher';
import { ThemeSwitcherDropdown } from './ThemeSwitcher';
import { useTheme } from '../contexts/ThemeContext';
import HRLogo from './HRLogo';

/* ═══════════════════════════════════════════════════════════════
   DESIGN TOKENS — Frosted Glass User Menu
   ═══════════════════════════════════════════════════════════════ */
const DT = {
  // glass surfaces — CSS variable references for theme-awareness
  menuBg:       'var(--menu-bg)',
  menuBorder:   'var(--menu-border)',
  subBg:        'var(--menu-sub-bg)',
  tooltipBg:    'var(--menu-tooltip-bg)',
  divider:      'var(--menu-divider)',
  hoverBg:      'var(--menu-hover-bg)',
  // text
  textPrimary:  'var(--menu-text-primary)',
  textSecondary:'var(--menu-text-secondary)',
  textMuted:    'var(--menu-text-muted)',
  // accent
  activeBg:     'var(--menu-active-bg)',
  activeBorder: 'var(--menu-active-border)',
  activeText:   'var(--menu-active-text)',
  // red logout
  logoutDefault:'var(--menu-logout-default)',
  logoutHover:  'var(--menu-logout-hover)',
  logoutHoverBg:'var(--menu-logout-hover-bg)',
  // pill trigger
  pillBg:       'var(--menu-pill-bg)',
  pillBorder:   'var(--menu-pill-border)',
  // online dot
  onlineDotBorder: 'var(--online-dot-border)',
  // avatar
  avatarBg:     'var(--avatar-bg)',
} as const;

const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'fr', name: 'Français' },
  { code: 'ar', name: 'العربية' },
  { code: 'es', name: 'Español' },
  { code: 'de', name: 'Deutsch' },
  { code: 'it', name: 'Italiano' },
];

/* ═══════════════════════════════════════════════════════════════ */

interface HeaderProps {
  navItems: NavItem[];
  onOpenAuthModal?: (mode: 'login' | 'signup') => void;
  onGoToDashboard?: () => void;
}

const Header: React.FC<HeaderProps> = ({ navItems, onOpenAuthModal, onGoToDashboard }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showLangSub, setShowLangSub] = useState(false);
  const { user, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const { theme, setTheme } = useTheme();
  const userMenuRef = useRef<HTMLDivElement>(null);

  const currentLang = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.documentElement.dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  // Close menu on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setShowUserMenu(false);
        setShowLangSub(false);
      }
    };
    if (showUserMenu) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showUserMenu]);

  const handleNavClick = (href: string) => {
    setIsMenuOpen(false);
    if (href === '#') { window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleLogout = async () => {
    setShowUserMenu(false);
    setIsMenuOpen(false);
    await logout();
  };

  const handleAuthClick = (mode: 'login' | 'signup') => {
    setIsMenuOpen(false);
    onOpenAuthModal?.(mode);
  };

  const displayName = user?.displayName || user?.email?.split('@')[0] || 'User';
  const initial = (user?.displayName?.[0] || user?.email?.[0] || 'U').toUpperCase();

  const getNavKey = (label: string) => ({
    'Home': 'common:home', 'How It Works': 'common:how_it_works',
    'Replica Directory': 'common:replica_directory', 'Trust': 'common:trust', 'Pricing': 'common:pricing',
  }[label] || label);

  /* ─── Theme buttons data ─── */
  const themeItems = [
    { id: 'light' as const, icon: Sun, label: 'Light' },
    { id: 'dark'  as const, icon: Moon, label: 'Dark' },
    { id: 'system'as const, icon: Monitor, label: 'System' },
  ];

  return (
    <header className={`fixed w-full z-50 transition-all duration-300 ${
      isScrolled || isMenuOpen
        ? 'bg-hig-bg-secondary/80 backdrop-blur-xl border-b border-hig-border'
        : 'bg-transparent border-b border-transparent'
    }`}>
      <div className="container-hig">
        <div dir="ltr" className="flex justify-between items-center h-16">

          {/* ── Logo ── */}
          <a href="#" className="flex items-center gap-2.5 group" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
            <HRLogo size={26} state="default" showGlow={false} />
            <span className="text-[15px] font-medium tracking-tight">
              <span className="text-hig-text-primary group-hover:opacity-80 transition-opacity">HumanReplica</span>
              <span className="text-hig-accent">.ai</span><span className="text-[15px] text-hig-text-secondary -translate-y-[3px] inline-block ml-[1px] font-normal">®</span>
            </span>
          </a>

          {/* ── Desktop Nav ── */}
          <nav className="hidden md:flex items-center gap-8">
            {navItems.map(item => (
              <a key={item.label} href={item.href}
                onClick={(e) => { if (item.href.startsWith('#')) { e.preventDefault(); handleNavClick(item.href); } }}
                className="text-sm text-hig-text-secondary hover:text-hig-text-primary transition-colors"
              >{t(getNavKey(item.label))}</a>
            ))}
          </nav>

          {/* ── Desktop Actions ── */}
          <div className="hidden md:flex items-center gap-3">
            {user ? (
              /* ════ LOGGED IN — Avatar pill + Frosted Glass Dropdown ════ */
              <div className="relative" ref={userMenuRef}>
                {/* Trigger pill */}
                <button
                  onClick={() => { setShowUserMenu(!showUserMenu); setShowLangSub(false); }}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-xl transition-all"
                  style={{
                    background: showUserMenu ? DT.menuBg : DT.pillBg,
                    border: `0.5px solid ${showUserMenu ? DT.menuBorder : DT.pillBorder}`,
                    backdropFilter: 'blur(12px)',
                  }}
                >
                  {/* Avatar */}
                  <div className="relative">
                    <div className="w-7 h-7 rounded-full flex items-center justify-center"
                      style={{ background: DT.avatarBg, border: `0.5px solid ${DT.activeBorder}` }}>
                      <span style={{ color: DT.activeText, fontSize: 11, fontWeight: 700 }}>{initial}</span>
                    </div>
                    {/* Online dot */}
                    <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-green-400 border-2" style={{ borderColor: DT.onlineDotBorder }} />
                  </div>
                  <span className="text-sm max-w-[120px] truncate" style={{ color: DT.textPrimary }}>{displayName}</span>
                  <ChevronDown size={14} style={{ color: DT.textSecondary, transition: 'transform 0.2s', transform: showUserMenu ? 'rotate(180deg)' : 'none' }} />
                </button>

                {/* ──── DROPDOWN ──── */}
                <AnimatePresence>
                  {showUserMenu && (
                    <motion.div
                      initial={{ opacity: 0, y: 8, scale: 0.96 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 8, scale: 0.96 }}
                      transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                      className="absolute right-0 top-full mt-2 z-50 overflow-visible"
                      style={{
                        width: 272,
                        background: DT.menuBg,
                        backdropFilter: 'blur(24px) saturate(180%)',
                        WebkitBackdropFilter: 'blur(24px) saturate(180%)',
                        border: `0.5px solid ${DT.menuBorder}`,
                        borderRadius: 16,
                        boxShadow: 'var(--menu-shadow)',
                      }}
                    >
                      {/* ── 1. Header — avatar + name + email ── */}
                      <div className="flex items-center gap-3 px-4 pt-4 pb-3" style={{ borderBottom: `0.5px solid ${DT.divider}` }}>
                        <div className="relative flex-shrink-0">
                          <div className="w-10 h-10 rounded-full flex items-center justify-center"
                            style={{ background: DT.avatarBg, border: `0.5px solid ${DT.activeBorder}` }}>
                            <span style={{ color: DT.activeText, fontSize: 15, fontWeight: 700 }}>{initial}</span>
                          </div>
                          <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-green-400 border-2" style={{ borderColor: DT.onlineDotBorder }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[13px] font-semibold truncate" style={{ color: DT.textPrimary }}>{displayName}</p>
                          <p className="text-[11px] truncate" style={{ color: DT.textSecondary }}>{user.email}</p>
                        </div>
                      </div>

                      {/* ── 2. Nav items — Dashboard + Account Settings ── */}
                      <div style={{ padding: '4px 0', borderBottom: `0.5px solid ${DT.divider}` }}>
                        {onGoToDashboard && (
                          <button onClick={() => { setShowUserMenu(false); onGoToDashboard(); }}
                            className="w-full flex items-center gap-3 px-4 py-2.5 transition-colors"
                            style={{ color: DT.textSecondary, fontSize: 13 }}
                            onMouseEnter={e => { (e.currentTarget.style.background = DT.hoverBg); (e.currentTarget.style.color = DT.textPrimary as string); }}
                            onMouseLeave={e => { (e.currentTarget.style.background = 'transparent'); (e.currentTarget.style.color = DT.textSecondary as string); }}
                          >
                            <LayoutDashboard size={16} />
                            {t('common:dashboard', 'Dashboard')}
                          </button>
                        )}
                        <button onClick={() => { setShowUserMenu(false); onGoToDashboard?.(); }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 transition-colors"
                          style={{ color: DT.textSecondary, fontSize: 13 }}
                          onMouseEnter={e => { (e.currentTarget.style.background = DT.hoverBg); (e.currentTarget.style.color = DT.textPrimary as string); }}
                          onMouseLeave={e => { (e.currentTarget.style.background = 'transparent'); (e.currentTarget.style.color = DT.textSecondary as string); }}
                        >
                          <Settings size={16} />
                          {t('common:account_settings', 'Account Settings')}
                        </button>
                      </div>

                      {/* ── 3. Theme selector — icon buttons with CSS tooltips ── */}
                      <div style={{ padding: '8px 16px', borderBottom: `0.5px solid ${DT.divider}` }}>
                        <p style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.09em', color: DT.textMuted, marginBottom: 8 }}>Theme</p>
                        <div className="flex items-center gap-1">
                          {themeItems.map(ti => {
                            const Icon = ti.icon;
                            const isActive = theme === ti.id;
                            return (
                              <div key={ti.id} className="relative group">
                                <button
                                  onClick={() => setTheme(ti.id)}
                                  className="flex items-center justify-center w-9 h-8 rounded-lg transition-all"
                                  style={{
                                    background: isActive ? DT.activeBg : 'transparent',
                                    border: isActive ? `0.5px solid ${DT.activeBorder}` : '0.5px solid transparent',
                                    color: isActive ? DT.activeText : (DT.textSecondary as string),
                                  }}
                                  onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = DT.hoverBg; }}
                                  onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                                >
                                  <Icon size={16} />
                                </button>
                                {/* CSS-like tooltip */}
                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 pointer-events-none
                                  opacity-0 group-hover:opacity-100 transition-opacity duration-150"
                                  style={{ whiteSpace: 'nowrap' }}
                                >
                                  <div className="px-2 py-1 rounded-md text-[11px] font-medium"
                                    style={{ background: DT.tooltipBg, backdropFilter: 'blur(12px)', border: `0.5px solid ${DT.menuBorder}`, color: DT.textPrimary }}>
                                    {ti.label}
                                  </div>
                                  {/* Caret */}
                                  <div className="flex justify-center -mt-[1px]">
                                    <div style={{ width: 0, height: 0, borderLeft: '4px solid transparent', borderRight: '4px solid transparent', borderTop: `4px solid ${DT.menuBorder}` }} />
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* ── 4. Language selector — row + animated submenu ── */}
                      <div style={{ borderBottom: `0.5px solid ${DT.divider}`, position: 'relative' }}>
                        <button
                          onClick={() => setShowLangSub(!showLangSub)}
                          className="w-full flex items-center gap-3 px-4 py-2.5 transition-colors"
                          style={{ color: showLangSub ? DT.activeText : (DT.textSecondary as string), fontSize: 13 }}
                          onMouseEnter={e => { e.currentTarget.style.background = DT.hoverBg; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                        >
                          <Globe size={16} />
                          <span className="flex-1 text-left">{currentLang.name}</span>
                          <ChevronDown size={14} style={{ transition: 'transform 0.2s', transform: showLangSub ? 'rotate(180deg)' : 'none' }} />
                        </button>

                        {/* Language submenu — slides in */}
                        <AnimatePresence>
                          {showLangSub && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
                              className="overflow-hidden"
                            >
                              <div style={{
                                margin: '0 8px 8px',
                                background: DT.subBg,
                                backdropFilter: 'blur(20px)',
                                border: `0.5px solid ${DT.menuBorder}`,
                                borderRadius: 12,
                                padding: '4px 0',
                              }}>
                                {LANGUAGES.map(lang => {
                                  const isActive = i18n.language === lang.code;
                                  return (
                                    <motion.button
                                      key={lang.code}
                                      initial={{ opacity: 0, x: -6 }}
                                      animate={{ opacity: 1, x: 0 }}
                                      transition={{ duration: 0.15, delay: 0.02 }}
                                      onClick={() => { i18n.changeLanguage(lang.code); setShowLangSub(false); }}
                                      className="w-full flex items-center gap-3 px-3 py-2 transition-colors"
                                      style={{ fontSize: 13, color: isActive ? DT.activeText : (DT.textSecondary as string) }}
                                      onMouseEnter={e => { e.currentTarget.style.background = DT.hoverBg; }}
                                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                                    >
                                      <span className="flex-1 text-left">{lang.name}</span>
                                      {isActive && <Check size={14} style={{ color: DT.activeText }} />}
                                    </motion.button>
                                  );
                                })}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>

                      {/* ── 5. Log out ── */}
                      <div>
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center gap-3 px-4 py-2.5 transition-colors"
                          style={{ color: DT.logoutDefault, fontSize: 13 }}
                          onMouseEnter={e => { (e.currentTarget.style.background = DT.logoutHoverBg); (e.currentTarget.style.color = DT.logoutHover); }}
                          onMouseLeave={e => { (e.currentTarget.style.background = 'transparent'); (e.currentTarget.style.color = DT.logoutDefault); }}
                        >
                          <LogOut size={16} />
                          {t('common:logout')}
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              /* ════ LOGGED OUT — Language + Theme visible ════ */
              <div className="flex items-center gap-3">
                <ThemeSwitcherDropdown />
                <LanguageSwitcher />
                <button onClick={() => handleAuthClick('login')}
                  className="text-sm text-hig-text-secondary hover:text-hig-text-primary transition-colors">
                  {t('common:login')}
                </button>
                <button onClick={() => handleAuthClick('signup')}
                  className="btn-primary text-sm px-5 py-2 rounded-xl">
                  {t('common:sign_up')}
                </button>
              </div>
            )}
          </div>

          {/* ── Mobile Menu Button ── */}
          <button className="md:hidden text-hig-text-primary" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* ── Mobile Navigation ── */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.nav
              className="md:hidden py-4 border-t border-hig-border overflow-hidden"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
            >
              <div className="flex flex-col gap-4">
                {navItems.map((item, index) => (
                  <motion.a key={item.label} href={item.href}
                    onClick={(e) => { e.preventDefault(); handleNavClick(item.href); }}
                    className="text-base text-hig-text-primary py-2"
                    initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.05 }}
                  >{t(getNavKey(item.label))}</motion.a>
                ))}

                <div className="h-px bg-hig-divider my-2" />
                <div className="py-2 flex items-center gap-3">
                  <ThemeSwitcherDropdown />
                  <LanguageSwitcher />
                </div>
                <div className="h-px bg-hig-divider my-2" />

                {user ? (
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <div className="w-9 h-9 rounded-full flex items-center justify-center"
                          style={{ background: DT.avatarBg, border: `0.5px solid ${DT.activeBorder}` }}>
                          <span style={{ color: DT.activeText, fontSize: 13, fontWeight: 700 }}>{initial}</span>
                        </div>
                        <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-green-400 border-2" style={{ borderColor: DT.onlineDotBorder }} />
                      </div>
                      <div>
                        <p className="text-sm text-hig-text-primary">{displayName}</p>
                        <p className="text-xs text-hig-text-tertiary">{user.email}</p>
                      </div>
                    </div>
                    {onGoToDashboard && (
                      <button onClick={() => { setIsMenuOpen(false); onGoToDashboard(); }}
                        className="btn-primary text-base px-6 py-3 rounded-xl">
                        {t('common:dashboard', 'Dashboard')}
                      </button>
                    )}
                    <button onClick={handleLogout} className="flex items-center gap-2 text-hig-text-secondary">
                      <LogOut className="h-5 w-5" /><span>{t('common:logout')}</span>
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col gap-3">
                    <button onClick={() => handleAuthClick('login')} className="text-base text-hig-text-primary py-2">{t('common:login')}</button>
                    <button onClick={() => handleAuthClick('signup')} className="btn-primary text-base px-6 py-3 rounded-xl">{t('common:sign_up')}</button>
                  </div>
                )}
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};

export default Header;